// Secondary button with an animated WUBRG gradient border. Visual companion
// to LiquidButton: same mana-color DNA, but quieter — solid dark fill + thin
// gradient frame that slowly rotates. Zero new deps, pure CSS.
//
// Use LiquidButton for primary CTAs ("Build your first deck"). Use this for
// paired secondary actions ("Sign in", "Cancel", "View details") so the two
// read as a family without fighting each other for attention.

import type React from "react";
import { cn } from "../../lib/cn";

export type MagicVariant = "default" | "ghost";

export interface MagicButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: MagicVariant;
  trailing?: React.ReactNode;
  /** If true, render a subtle inner glow that pulses once per animation cycle. */
  pulse?: boolean;
}

export default function MagicButton({
  variant = "default",
  trailing,
  pulse = false,
  className,
  children,
  ...rest
}: MagicButtonProps) {
  return (
    <span
      className={cn(
        "relative inline-block h-[2.85em] group rounded-lg p-[1.5px]",
        // The gradient border lives in the wrapper's background. Using
        // conic-gradient + keyframe rotation matches the WUBRG foil
        // vocabulary without needing motion/gsap.
        "magic-border",
        variant === "ghost" && "magic-border--ghost",
        className,
      )}
    >
      <button
        type="button"
        {...rest}
        className={cn(
          "relative block h-full w-full rounded-[6px] px-4",
          "bg-bg-surface text-fg font-medium tracking-wide",
          "transition-colors duration-200",
          "hover:bg-bg-raised",
          "disabled:cursor-not-allowed disabled:opacity-40",
          // Ghost variant has a translucent fill so the border becomes
          // the primary visual element.
          variant === "ghost" && "bg-bg-base/50 hover:bg-bg-raised/40",
          pulse && "magic-pulse",
        )}
      >
        <span className="flex items-center justify-center gap-2 whitespace-nowrap">
          {children}
          {trailing}
        </span>
      </button>
    </span>
  );
}
